// Generative-UI renderer for agent-authored Mermaid diagram syntax
// (flowcharts, sequence diagrams, Gantt, state diagrams, ...). Open-ended
// counterpart to ChartArtifactCard — `syntax` is the raw Mermaid grammar
// string; mermaid.render() interprets it (no eval) and returns SVG markup.
// mermaid.render is async, so we track loading/error state around it.

import { useDeferredValue, useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';

const actionButtonClassName =
  'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground';

// `syntax` streams in token-by-token while the agent is still emitting the
// diagram; re-parsing on every partial delta both wastes work and flashes
// invalid-intermediate-syntax errors. useDeferredValue lets React coalesce
// rapid updates and only commit a render once the syntax stops changing for
// a beat, without needing a manual setTimeout debounce.
let idCounter = 0;

export function Mermaid({ syntax }: { syntax: string }) {
  const deferredSyntax = useDeferredValue(syntax);
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(`mermaid-${++idCounter}`);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setSvg(null);

    import('mermaid').then(({ default: mermaid }) => {
      // securityLevel: 'strict' disables mermaid's click-handler/HTML-tag
      // features (tooltips, hrefs, embedded HTML in labels) since this
      // renders agent-authored, not developer-authored, diagram syntax.
      // Theme follows the app's own light/dark toggle (see ThemeContext.tsx,
      // which sets `data-color-scheme` on <html> — same convention as
      // tailwind.css's `dark:` variant) rather than mermaid's 'neutral'.
      const isDark = document.documentElement.getAttribute('data-color-scheme') === 'dark';
      mermaid.initialize({ startOnLoad: false, theme: isDark ? 'dark' : 'neutral', securityLevel: 'strict' });
      mermaid
        .render(idRef.current, deferredSyntax)
        .then(({ svg: rendered }) => {
          if (!cancelled) setSvg(rendered);
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(err instanceof Error ? err.message : 'Invalid Mermaid syntax');
        });
    });

    return () => {
      cancelled = true;
    };
  }, [deferredSyntax]);

  function handleDownload() {
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diagram.svg';
    a.click();
    URL.revokeObjectURL(url);
    // TODO: hook a "share as persistent artifact" action here once a
    // download/artifact API exists for this content type.
  }

  if (error) {
    return (
      <div className="my-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
        Couldn't render diagram: {error}
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-border bg-card p-3">
      <div className="mb-1 flex justify-end">
        <button
          type="button"
          aria-label="Download diagram"
          className={actionButtonClassName}
          onClick={handleDownload}
          disabled={!svg}
        >
          <Download className="size-4" />
        </button>
      </div>
      {svg ? (
        // mermaid.render() output is self-generated SVG from a grammar
        // interpreter (no attacker-controlled HTML/script), so it does not
        // go through the sanitizeHtml path that raw agent-authored markup
        // (see Svg.tsx) requires.
        <div dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <p className="text-xs text-muted-foreground">Rendering diagram…</p>
      )}
    </div>
  );
}
